import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppDispatch } from "@/app/hooks";
import { useNotification } from "@/context/NotificationContext";
import { CourseContentBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/CourseContentBlockEditor";
import {
  CourseFormBlock,
  CourseFormModule,
} from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import { saveCourseModuleToLibraryAction } from "@/modules/learningCenter/features/ModuleLibrary/slices/createUpdateLibraryModuleSlice";
import { TagsInput } from "@/modules/learningCenter/features/ModuleLibrary/components/TagsInput/TagsInput";
import { LIBRARY_MODULE_DIFFICULTY_OPTIONS } from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

/** Sentinel for the "no difficulty" Select option (Select can't hold ""). */
const NONE_VALUE = "__none__";

interface CourseModulePaneProps {
  module: CourseFormModule;
  index: number;
  canDelete: boolean;
  onChange: (module: CourseFormModule) => void;
  onDelete: () => void;
}

/**
 * The right-pane editor for a single selected course module: rename/describe,
 * author content blocks (via the shared CourseContentBlockEditor with
 * add-new/add-from-library/save-to-library affordances), and delete.
 */
export function CourseModulePane({
  module,
  index,
  canDelete,
  onChange,
  onDelete,
}: CourseModulePaneProps) {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const handleField = useCallback(
    (field: "title" | "description", value: string) => {
      onChange({ ...module, [field]: value });
    },
    [module, onChange],
  );

  const handleBlocksChange = useCallback(
    (blocks: CourseFormBlock[]) => {
      onChange({ ...module, blocks });
    },
    [module, onChange],
  );

  const handleDifficulty = useCallback(
    (value: string) => {
      onChange({
        ...module,
        difficulty: value === NONE_VALUE ? null : value,
      });
    },
    [module, onChange],
  );

  const handleEstimatedMinutes = useCallback(
    (value: string) => {
      onChange({
        ...module,
        estimatedMinutes: value === "" ? null : Number(value),
      });
    },
    [module, onChange],
  );

  const handleTags = useCallback(
    (tags: string[]) => {
      onChange({ ...module, tags });
    },
    [module, onChange],
  );

  const handleSaveToLibrary = useCallback(() => {
    if (!module.uuid) {
      return;
    }
    dispatch(
      saveCourseModuleToLibraryAction(
        {
          courseModuleUuid: module.uuid,
          title: module.title,
          description: module.description || null,
          difficulty: module.difficulty || null,
          estimatedMinutes: module.estimatedMinutes ?? null,
          tags: module.tags,
        },
        (saved) => {
          showNotification(
            "Saved to library",
            `"${saved.title}" was saved to the library as a draft.`,
            "success",
          );
        },
      ),
    );
  }, [dispatch, module, showNotification]);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            Module {index + 1}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure this module&apos;s content.
          </p>
        </div>
        {module.uuid && (
          <Button
            onClick={handleSaveToLibrary}
            size="sm"
            type="button"
            variant="outline"
          >
            Save to Library
          </Button>
        )}
      </div>

      <div className="space-y-3">
        <Input
          aria-label={`Module ${index + 1} title`}
          onChange={(e) => handleField("title", e.target.value)}
          placeholder="Module title"
          value={module.title}
        />
        <Textarea
          aria-label={`Module ${index + 1} description`}
          onChange={(e) => handleField("description", e.target.value)}
          placeholder="Module description (optional)"
          value={module.description ?? ""}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-900 dark:text-white">
            Difficulty
          </label>
          <Select
            onValueChange={handleDifficulty}
            value={module.difficulty || NONE_VALUE}
          >
            <SelectTrigger aria-label={`Module ${index + 1} difficulty`}>
              <SelectValue placeholder="Select difficulty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None</SelectItem>
              {LIBRARY_MODULE_DIFFICULTY_OPTIONS.map((difficulty) => (
                <SelectItem key={difficulty} value={difficulty}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-900 dark:text-white">
            Estimated minutes
          </label>
          <Input
            aria-label={`Module ${index + 1} estimated minutes`}
            min={0}
            onChange={(e) => handleEstimatedMinutes(e.target.value)}
            placeholder="e.g. 30"
            type="number"
            value={module.estimatedMinutes ?? ""}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-900 dark:text-white">
          Tags
        </label>
        <TagsInput onChange={handleTags} value={module.tags} />
      </div>

      {module.sourceLibraryModuleUuid && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Content will be copied from the selected library module when you save
          the course.
        </p>
      )}

      <CourseContentBlockEditor
        blocks={module.blocks}
        onChange={handleBlocksChange}
      />

      <div className="flex justify-start pt-2 border-t border-gray-200 dark:border-slate-700">
        <Button
          aria-label={`Remove module ${index + 1}`}
          disabled={!canDelete}
          onClick={onDelete}
          title={
            canDelete
              ? "Remove module"
              : "A course must keep at least one module"
          }
          type="button"
          variant="outline"
        >
          Remove Module
        </Button>
      </div>
    </div>
  );
}
